import { useEffect, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Home, GraduationCap, Users, Trees, Plus, Trash2, MapPinned, ShieldCheck } from "lucide-react";
import api from "@/lib/api";

const PLACE_TYPES = [
  { id: "casa", label: "Casa", icon: Home },
  { id: "cole", label: "El cole", icon: GraduationCap },
  { id: "abuelos", label: "Los abuelos", icon: Users },
  { id: "parque", label: "El parque", icon: Trees },
];

const iconFor = (t) => (PLACE_TYPES.find((p) => p.id === t) || PLACE_TYPES[0]).icon;

export default function SafeZones() {
  const [zones, setZones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState("");
  const [placeType, setPlaceType] = useState("cole");
  const [radius, setRadius] = useState(200);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/safe-zones");
      setZones(data);
    } catch (e) {
      setZones([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!name.trim()) return;
    setSaving(true);
    try {
      const { data } = await api.post("/safe-zones", { name: name.trim(), place_type: placeType, radius });
      setZones((z) => [...z, data]);
      setName("");
    } catch (e) {}
    setSaving(false);
  };

  const remove = async (id) => {
    setZones((z) => z.filter((x) => x.zone_id !== id));
    try { await api.delete(`/safe-zones/${id}`); } catch (e) { load(); }
  };

  return (
    <section className="mt-16" data-testid="safe-zones">
      <span className="text-[11px] font-bold uppercase tracking-[0.16em] text-[#2E9EF0]">Demo</span>
      <h2 className="mt-2 text-[24px] font-black text-[#12263A]">Tus zonas seguras</h2>
      <p className="mt-1 text-[15px] text-[#6E7B8B] max-w-lg">
        Marca los lugares de confianza. Recibirás un aviso amable cuando tu peque entre o salga. Nada de vigilancia.
      </p>

      {/* Create form */}
      <div className="mt-6 bg-white border-2 border-[#12263A] p-6" style={{ borderRadius: "24px" }}>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="text-[11px] font-bold uppercase tracking-[0.16em] text-[#6E7B8B]">Nombre del lugar</label>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && add()}
              placeholder="Ej. Casa de la abuela"
              data-testid="zone-name-input"
              className="mt-2 w-full px-4 py-3 border-2 border-[#12263A] rounded-[16px] text-[15px] text-[#12263A] outline-none focus:border-[#2E9EF0]"
            />
          </div>
          <div>
            <label className="text-[11px] font-bold uppercase tracking-[0.16em] text-[#6E7B8B]">Radio: {radius} m</label>
            <input
              type="range" min="100" max="1000" step="50" value={radius}
              onChange={(e) => setRadius(Number(e.target.value))}
              data-testid="zone-radius-input"
              className="mt-4 w-full accent-[#2E9EF0]"
            />
          </div>
        </div>

        <div className="mt-4 flex flex-wrap gap-2">
          {PLACE_TYPES.map((p) => {
            const active = placeType === p.id;
            const Icon = p.icon;
            return (
              <button
                key={p.id}
                onClick={() => setPlaceType(p.id)}
                data-testid={`zone-type-${p.id}`}
                className={`flex items-center gap-2 px-4 py-2 rounded-[14px] border-2 border-[#12263A] font-bold text-[14px] transition-colors ${
                  active ? "bg-[#2E9EF0] text-[#12263A]" : "bg-[#F3F2F2] text-[#12263A] hover:bg-[#D6D9DC]"
                }`}
              >
                <Icon size={18} strokeWidth={2} /> {p.label}
              </button>
            );
          })}
        </div>

        <button
          onClick={add}
          disabled={saving || !name.trim()}
          data-testid="zone-add-btn"
          className="mt-5 flex items-center gap-2 px-6 py-3 rounded-[16px] bg-[#12263A] text-white border-2 border-[#12263A] font-bold hover:bg-[#2E9EF0] hover:text-[#12263A] transition-colors disabled:opacity-50"
        >
          <Plus size={18} strokeWidth={2.5} /> Crear zona segura
        </button>
      </div>

      {/* Zones list */}
      {loading ? (
        <p className="mt-6 text-[15px] text-[#6E7B8B]">Cargando zonas…</p>
      ) : zones.length === 0 ? (
        <div className="mt-6 flex items-center gap-3 text-[15px] text-[#6E7B8B]">
          <MapPinned size={20} strokeWidth={2} /> Aún no has creado ninguna zona segura.
        </div>
      ) : (
        <div className="mt-6 grid sm:grid-cols-2 gap-4">
          <AnimatePresence>
            {zones.map((z) => {
              const Icon = iconFor(z.place_type);
              return (
                <motion.div
                  key={z.zone_id}
                  layout
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  transition={{ duration: 0.25 }}
                  data-testid="zone-card"
                  className="relative bg-white border-2 border-[#12263A] p-6 flex items-center gap-4 overflow-hidden"
                  style={{ borderRadius: "24px" }}
                >
                  {/* flat concentric "radius" rings */}
                  <div className="relative w-16 h-16 shrink-0">
                    <span className="absolute inset-0 border-2 border-[#D6D9DC]" style={{ borderRadius: "31%" }} />
                    <span className="absolute inset-2 border-2 border-[#2E9EF0]" style={{ borderRadius: "31%" }} />
                    <span className="absolute inset-0 flex items-center justify-center">
                      <Icon size={22} strokeWidth={2} className="text-[#12263A]" />
                    </span>
                  </div>
                  <div className="flex-1">
                    <span className="block font-black text-[#12263A] text-[17px]">{z.name}</span>
                    <span className="inline-flex items-center gap-1 mt-1 text-[11px] font-bold uppercase tracking-[0.12em] text-[#12263A] bg-[#3CB878] border-2 border-[#12263A] rounded-full px-2 py-0.5">
                      <ShieldCheck size={13} strokeWidth={2.5} /> En zona segura
                    </span>
                    <span className="block text-[13px] text-[#6E7B8B] mt-1">Radio {z.radius} m</span>
                  </div>
                  <button
                    onClick={() => remove(z.zone_id)}
                    data-testid={`zone-delete-${z.zone_id}`}
                    aria-label={`Eliminar ${z.name}`}
                    className="w-10 h-10 flex items-center justify-center border-2 border-[#12263A] rounded-[12px] text-[#12263A] hover:bg-[#FF6B57] transition-colors"
                  >
                    <Trash2 size={18} strokeWidth={2} />
                  </button>
                </motion.div>
              );
            })}
          </AnimatePresence>
        </div>
      )}
    </section>
  );
}
